import { Button, Table } from "antd";
import type { TableProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import { getRecordsRegisterStatusView } from "../../../../model";
import type { SortDirection, SortField } from "../../../../model/hooks/use-sort-state";
import type { MockRecordRow } from "../../../../model/mock-data";
import { RegistryStatusPill } from "../registry-status-pill";
import "./registry-table.scss";

const staticColumns: ColumnsType<MockRecordRow> = [
  {
    title: "Действия",
    key: "actions",
    width: 120,
    render: () => <span className="registry-table__muted">-</span>,
  },
  {
    title: "Наименование акта",
    dataIndex: "fullName",
    key: "fullName",
    width: 200,
    render: (value: string) => (
      <Button type="link" className="registry-table__link-btn">
        {value}
      </Button>
    ),
  },
  {
    title: "Дата выдачи",
    dataIndex: "actDate",
    key: "actDate",
    width: 140,
    sorter: true,
  },
  {
    title: "Наименование работы",
    dataIndex: "workName",
    key: "workName",
    width: 380,
  },
  {
    title: "Вид работы",
    dataIndex: "workType",
    key: "workType",
    width: 180,
  },
  {
    title: "Статус",
    dataIndex: "approvalStatus",
    key: "approvalStatus",
    width: 140,
    render: (value: string) => {
      const view = getRecordsRegisterStatusView(value);
      return (
        <RegistryStatusPill
          textColor={view.textColor}
          backgroundColor={view.backgroundColor}
        >
          {view.label}
        </RegistryStatusPill>
      );
    },
  },
  {
    title: "Замечания",
    dataIndex: "notes",
    key: "notes",
    width: 160,
    render: (value: string) => <span className="registry-table__muted">{value}</span>,
  },
  {
    title: "Закрытие",
    dataIndex: "closing",
    key: "closing",
    width: 380,
    render: (value: string) => (
      <Button type="link" className="registry-table__link-btn">
        {value}
      </Button>
    ),
  },
  {
    title: "Автор",
    dataIndex: "creatorName",
    key: "creatorName",
    width: 200,
    render: (value: string) => (
      <span className="registry-table__multiline">{value}</span>
    ),
  },
  {
    title: "Подписант",
    dataIndex: "assigneeNames",
    key: "assigneeNames",
    width: 220,
    render: (value: string) => (
      <span className="registry-table__multiline">{value}</span>
    ),
  },
  {
    title: "Проверяющий",
    dataIndex: "inspectorNames",
    key: "inspectorNames",
    width: 220,
    render: (value: string) => (
      <span className="registry-table__multiline">{value}</span>
    ),
  },
  {
    title: "Раздел проекта",
    dataIndex: "sectionName",
    key: "sectionName",
    width: 160,
  },
  {
    title: "Конструкция",
    dataIndex: "currentWork",
    key: "currentWork",
    width: 140,
  },
  {
    title: "Договор",
    dataIndex: "contract",
    key: "contract",
    width: 120,
  },
  {
    title: "КС-2",
    dataIndex: "ks2",
    key: "ks2",
    width: 100,
  },
];

interface RegistryTableProps {
  data: MockRecordRow[];
  isLoading: boolean;
  sortDirection: SortDirection;
  onSortChange: (field: SortField, direction: SortDirection) => void;
}

const SORT_FIELD_MAP: Record<string, SortField> = {
  actDate: "ACT_DATE",
};

export function RegistryTable({ data, isLoading, sortDirection, onSortChange }: RegistryTableProps) {
  const columns: ColumnsType<MockRecordRow> = staticColumns.map((col) => {
    if ("key" in col && col.key === "actDate") {
      return { ...col, sortOrder: sortDirection === "ASC" ? "ascend" : "descend" };
    }
    return col;
  });

  const handleChange: TableProps<MockRecordRow>["onChange"] = (_, __, sorter) => {
    if (Array.isArray(sorter) || !sorter.columnKey) return;
    const field = SORT_FIELD_MAP[String(sorter.columnKey)];
    if (!field) return;

    // Ant Design хочет "отменить" сортировку (order = undefined) —
    // вместо этого переключаем направление, чтобы сортировка всегда была активна
    let nextDirection: SortDirection;
    if (sorter.order === "ascend") {
      nextDirection = "ASC";
    } else if (sorter.order === "descend") {
      nextDirection = "DESC";
    } else {
      nextDirection = sortDirection === "ASC" ? "DESC" : "ASC";
    }

    onSortChange(field, nextDirection);
  };

  return (
    <div className="registry-table">
      <Table<MockRecordRow>
        className="registry-table__inner"
        columns={columns}
        dataSource={data}
        bordered={false}
        rowKey="key"
        loading={isLoading}
        scroll={{ x: 2860 }}
        pagination={false}
        onChange={handleChange}
      />
    </div>
  );
}
